import { type FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useForgotPasswordMutation } from '@/hooks/useAuthActions'

const ForgotPassword = () => {
    const [email, setEmail] = useState('')
    const navigate = useNavigate()
    const forgotPassword = useForgotPasswordMutation()

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        forgotPassword.mutate({ email })
    }

    return (
        <div className="space-y-8">
            <div className="space-y-2">
                <button
                    onClick={() => navigate('/auth/sign-in')}
                    className="mb-2 flex items-center gap-1 text-sm font-medium text-[#717784] transition-colors hover:text-[#0C1324]"
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back to sign in
                </button>
                <h1 className="text-[22px] font-semibold text-[#0C1324]">
                    Forgot password
                </h1>
                <p className="text-sm text-[#6F7785]">
                    Enter the email attached to your account and a link to reset
                    your password will be sent.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                    type="email"
                    placeholder="Enter email"
                    className="rounded-[12px]"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    required
                />

                {forgotPassword.isSuccess ? (
                    <div className="rounded-2xl bg-[#E1F6F8] px-4 py-3 text-sm text-[#0C1324]">
                        Password reset link has been sent to your email. Please
                        check your inbox.
                    </div>
                ) : null}

                {forgotPassword.isError ? (
                    <p className="text-sm text-destructive">
                        {(forgotPassword.error as Error)?.message ??
                            'Unable to find that email.'}
                    </p>
                ) : null}

                <Button
                    type="submit"
                    disabled={
                        forgotPassword.isPending || forgotPassword.isSuccess
                    }
                    className="h-12 w-full rounded-full bg-[#0FA3B1] text-base font-semibold hover:bg-[#0C8D98]"
                >
                    {forgotPassword.isPending
                        ? 'Sending...'
                        : forgotPassword.isSuccess
                          ? 'Link sent'
                          : 'Reset password'}
                </Button>
            </form>
        </div>
    )
}

export default ForgotPassword
